import * as React from "react";
import { api, type BranchOut } from "../api";

const STORAGE_KEY = "selected_branch_id";

interface BranchContextValue {
  /** Faol filiallar ro'yxati (selektor uchun). */
  branches: BranchOut[];
  /**
   * Arxivlanganlari bilan BIRGA to'liq ro'yxat — nom qidirish uchun.
   *
   * Hujjat (xarid/sotuv/ish) arxivlangan filialga tegishli bo'lishi mumkin;
   * faqat faol ro'yxatdan qidirilsa u nomsiz qolardi. Selektor esa baribir
   * `branches` (faol) ustidan quriladi.
   */
  allBranches: BranchOut[];
  /** Tanlangan filial; null = "Barcha filiallar". */
  selectedBranchId: number | null;
  setBranch: (id: number | null) => void;
  /** Selektor faqat >1 faol filial bo'lganda ko'rsatiladi. */
  showSelector: boolean;
  reload: () => Promise<void>;
}

const BranchContext = React.createContext<BranchContextValue | null>(null);

function readStored(): number | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function BranchProvider({ children }: { children: React.ReactNode }) {
  const [branches, setBranches] = React.useState<BranchOut[]>([]);
  const [allBranches, setAllBranches] = React.useState<BranchOut[]>([]);
  const [selectedBranchId, setSelected] = React.useState<number | null>(readStored());

  const reload = React.useCallback(async () => {
    try {
      const all = await api.branches();
      const active = all.filter((b) => b.is_active);
      setAllBranches(all);
      setBranches(active);
      // Tanlangan filial endi mavjud/faol bo'lmasa — "Barcha filiallar"ga qaytamiz.
      setSelected((cur) =>
        cur != null && !active.some((b) => b.id === cur) ? null : cur,
      );
    } catch {
      setAllBranches([]);
      setBranches([]);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const setBranch = React.useCallback((id: number | null) => {
    setSelected(id);
    if (id == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  // >1 faol filial bo'lmasa selektor keraksiz (yagona filial — global = filial).
  const showSelector = branches.length > 1;
  // Selektor ko'rinmasa effektiv tanlov doim "barcha" bo'lishi kerak.
  const effectiveId = showSelector ? selectedBranchId : null;

  return (
    <BranchContext.Provider
      value={{
        branches,
        allBranches,
        selectedBranchId: effectiveId,
        setBranch,
        showSelector,
        reload,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const ctx = React.useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used inside BranchProvider");
  return ctx;
}

/**
 * `branch_id` → filial (ARXIVLANGANLARI bilan birga).
 *
 * `null` (filialsiz eski yozuv) va topilmagan id — ikkalasi ham `null`
 * qaytaradi, ya'ni chaqiruvchi "—" chizadi. Har chaqiruvda `Map` qurmaslik
 * uchun `useMemo` ichida.
 */
export function useBranchLookup(): (id: number | null | undefined) => BranchOut | null {
  const { allBranches } = useBranch();
  return React.useMemo(() => {
    const byId = new Map(allBranches.map((b) => [b.id, b]));
    return (id) => (id == null ? null : (byId.get(id) ?? null));
  }, [allBranches]);
}
